import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  first,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import * as TasksActions from './tasks.actions';
import { TaskManagementHttpService } from '@app/core/async-services/http/versioned/task-management/task-management.http';
import { Store } from '@ngrx/store';
import { State } from '@app/store/store.state';
import { API } from '@app/core/models';
import { LoadingController, NavController } from '@ionic/angular';
import {
  CreateOrEditRoomCleanTaskNoteBody,
  EditTaskBody,
  RoomCleanTaskStatus,
  Status,
} from '@app/core/async-services/http/versioned/task-management/task-management.interface';
import * as TasksSelectors from './tasks.selectors';
import * as UserSelectors from '@app/store/user/user.selectors';
import { Router } from '@angular/router';
import { HousekeepingHttpService } from '@app/core/async-services/http/versioned/housekeeping/housekeeping.http';
import { HotelsService } from '@app/core/services/hotels/hotels.service';
import { TaskService } from '../services/task.service';
import { filterRequiredObject } from '@app/core/utils/rxjs.util';

@Injectable()
export class Effects {
  determineDefaultTasksMenu$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.tasksInitialized),
      switchMap(() => this._hotelsService.taskHotels$.pipe(first())),
      withLatestFrom(this._store.select(TasksSelectors.selectTasksHotel)),
      map(([hotels, currentHotel]) => {
        const hotel = currentHotel || hotels[0];
        const taskFilter = this._taskService.getFilterOptions(hotel.entId)[0];
        return TasksActions.defaultTaskMenuDetermined({ hotel, taskFilter });
      }),
    ),
  );

  determineDefaultRealtimeRoomsMenu$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.realtimeRoomsInitialized),
      switchMap(() => this._hotelsService.realtimeRoomsHotels$.pipe(first())),
      withLatestFrom(
        this._store.select(TasksSelectors.selectRealtimeRoomsHotel),
      ),
      map(([hotels, currentHotel]) => {
        const hotel = currentHotel || hotels[0];
        return TasksActions.defaultRealtimeRoomsMenuDetermined({ hotel });
      }),
    ),
  );

  deleteTask$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.deleteTaskButtonClicked),
      withLatestFrom(this._store.select(TasksSelectors.selectActiveTaskId)),
      switchMap(([_, id]) =>
        this._taskManagementHttpService.deleteTask({ taskId: id }).pipe(
          map(() => TasksActions.deleteTaskSucceeded({ id })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  getTaskInboxChannel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.channelServiceForTasksInitialized),
      switchMap(() =>
        this._taskManagementHttpService.getTaskInboxChannel().pipe(
          map((channel) => {
            return TasksActions.getTaskInboxChannelSucceeded({
              channel,
            });
          }),
          catchError((response: API.Response) =>
            of(
              TasksActions.getTaskInboxChannelFailed({
                message: response.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getRoomCleanTaskInboxChannel$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.channelServiceForRoomCleanTasksInitialized),
      switchMap(() =>
        this._taskManagementHttpService.getRoomCleanTaskInboxChannel().pipe(
          map((channel) => {
            return TasksActions.getRoomCleanTaskInboxChannelSucceeded({
              channel,
            });
          }),
          catchError((response: API.Response) =>
            of(
              TasksActions.getRoomCleanTaskInboxChannelFailed({
                message: response.message,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getTaskList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.defaultTaskMenuDetermined,
        TasksActions.tasksMenuDoneButtonClicked,
      ),
      switchMap(({ hotel }) => {
        return this._taskManagementHttpService.getTaskList(hotel.entId).pipe(
          map((response) =>
            TasksActions.getTaskListSucceeded({
              taskList: response,
            }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  fetchTaskDetails$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.viewTaskPageEntered,
        TasksActions.viewRoomCleanTaskPageInitialized,
      ),
      withLatestFrom(this._store.select(TasksSelectors.selectActiveTaskId)),
      switchMap(([_, id]) =>
        this._taskManagementHttpService.getTaskDetails({ taskId: id }).pipe(
          map((payload) => TasksActions.getTaskDetailsSucceeded({ payload })),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  getNewTaskData$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.defaultTaskMenuDetermined,
        TasksActions.tasksMenuDoneButtonClicked,
        TasksActions.defaultRealtimeRoomsMenuDetermined,
        TasksActions.realtimeRoomsMenuSubmitted,
      ),
      switchMap(({ hotel }) => {
        return this._taskManagementHttpService.getNewTaskData(hotel.entId).pipe(
          map((newTaskData) =>
            TasksActions.getNewTaskDataSucceeded({
              newTaskData,
            }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  updateTaskAssignee$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.assigneeSaveButtonClicked),
      withLatestFrom(
        this._store.select(TasksSelectors.selectEntId),
        this._store.select(TasksSelectors.selectActiveTaskId),
      ),
      map(([{ assignee }, entId, taskId]) => ({
        assigneeId: assignee.id,
        assigneeTypeId: assignee.typeId,
        entId,
        taskId,
      })),
      filterRequiredObject(),
      switchMap(({ assigneeId, assigneeTypeId, entId, taskId }) => {
        const body: Partial<EditTaskBody> = {
          assigneeId,
          assigneeTypeId,
          entId,
          taskId,
        };
        return this._taskManagementHttpService.editTask(body).pipe(
          map((task) =>
            TasksActions.editAssigneeSucceeded({ id: taskId, task }),
          ),
          catchError(() => of(TasksActions.editAssigneeFailed())),
        );
      }),
    ),
  );

  createTask$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.newTaskPageCreateTaskButtonClicked),
      switchMap(({ body }) =>
        this._taskManagementHttpService.createTask(body).pipe(
          map((task) => TasksActions.createTaskSucceeded({ task })),
          catchError(() => of(TasksActions.createTaskFailed())),
        ),
      ),
    ),
  );

  navigateToTaskList$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          TasksActions.createTaskSucceeded,
          TasksActions.editAssigneeSucceeded,
          TasksActions.updateTaskStatusToDoneSucceeded,
          TasksActions.deleteTaskSucceeded,
        ),
        switchMap(() => this._navController.navigateRoot('app/tasks')),
      ),
    { dispatch: false },
  );

  navigateToTaskHome$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          TasksActions.pubNubTaskDeleted,
          TasksActions.pubNubRoomCleanTaskDeleted,
        ),
        switchMap(() => {
          const isOnViewTasksPage =
            this._router.url.startsWith('/app/tasks/view');
          if (isOnViewTasksPage) {
            return this._navController.navigateRoot('app/tasks');
          } else {
            return EMPTY;
          }
        }),
      ),
    { dispatch: false },
  );

  showLoadingOverlay$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          TasksActions.newTaskPageCreateTaskButtonClicked,
          TasksActions.assigneeSaveButtonClicked,
          TasksActions.markAsDoneButtonClicked,
          TasksActions.moveBackToInProgressButtonClicked,
          TasksActions.moveBackToOpenButtonClicked,
          TasksActions.startProgressButtonClicked,
          TasksActions.pmsStatusSaveButtonClicked,
          TasksActions.changeLinenSaveButtonClicked,
          TasksActions.roomCleanAssigneeSaveButtonClicked,
          TasksActions.roomCleanTaskStatusSaveButtonClicked,
          TasksActions.updateTaskStatusActionSheetOptionClicked,
          TasksActions.saveNoteclicked,
        ),
        tap(() => {
          this._showLoadingOverlay();
        }),
      ),
    { dispatch: false },
  );

  hideLoadingOverlay$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(
          TasksActions.createTaskFailed,
          TasksActions.createTaskSucceeded,
          TasksActions.editAssigneeFailed,
          TasksActions.editAssigneeSucceeded,
          TasksActions.updateTaskStatusSucceeded,
          TasksActions.updateTaskStatusFailed,
          TasksActions.updateTaskStatusToDoneSucceeded,
          TasksActions.updateTaskStatusToInProgressSucceeded,
          TasksActions.updateTaskStatusToOpenSucceeded,
          TasksActions.updatePMSStatusSucceeded,
          TasksActions.updatePMSStatusFailed,
          TasksActions.updateChangeLinenSucceeded,
          TasksActions.updateChangeLinenFailed,
          TasksActions.updateRoomCleanTaskAssigneeSucceeded,
          TasksActions.updateRoomCleanTaskAssigneeFailed,
          TasksActions.createOrEditTaskNoteFailed,
          TasksActions.createOrEditTaskNoteSucceeded,
        ),
        tap(() => {
          this._hideLoadingOverlay();
        }),
      ),
    { dispatch: false },
  );

  updateTaskStatusInProgress$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.startProgressButtonClicked,
        TasksActions.moveBackToInProgressButtonClicked,
      ),
      withLatestFrom(
        this._store.select(
          TasksSelectors.selectUpdateTaskStatusBody(Status.IN_PROGRESS),
        ),
      ),
      map(([_, body]) => body),
      filterRequiredObject(),
      switchMap((body) =>
        this._taskManagementHttpService.updateTaskStatus(body).pipe(
          map((updatedTaskStatusData) =>
            TasksActions.updateTaskStatusToInProgressSucceeded({
              id: body.taskId,
              updatedTaskStatusData,
            }),
          ),
          catchError(() => of(TasksActions.updateTaskStatusFailed())),
        ),
      ),
    ),
  );

  updateTaskStatusDone$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.markAsDoneButtonClicked,
        TasksActions.doneButtonClicked,
      ),
      withLatestFrom(
        this._store.select(
          TasksSelectors.selectUpdateTaskStatusBody(RoomCleanTaskStatus.DONE),
        ),
      ),
      map(([_, body]) => body),
      filterRequiredObject(),
      switchMap((body) =>
        this._taskManagementHttpService.updateTaskStatus(body).pipe(
          map((updatedTaskStatusData) =>
            TasksActions.updateTaskStatusToDoneSucceeded({
              id: body.taskId,
              updatedTaskStatusData,
            }),
          ),
          catchError(() => of(TasksActions.updateTaskStatusFailed())),
        ),
      ),
    ),
  );

  updateTaskStatusOpen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.moveBackToOpenButtonClicked),
      withLatestFrom(
        this._store.select(
          TasksSelectors.selectUpdateTaskStatusBody(Status.OPEN),
        ),
      ),
      map(([_, body]) => body),
      filterRequiredObject(),
      switchMap((body) =>
        this._taskManagementHttpService.updateTaskStatus(body).pipe(
          map((updatedTaskStatusData) =>
            TasksActions.updateTaskStatusToOpenSucceeded({
              id: body.taskId,
              updatedTaskStatusData,
            }),
          ),
          catchError(() => of(TasksActions.updateTaskStatusFailed())),
        ),
      ),
    ),
  );

  fetchRoomCleanTaskList$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.defaultRealtimeRoomsMenuDetermined,
        TasksActions.realtimeRoomsMenuSubmitted,
        TasksActions.realtimeRoomsPortfolioHotelClicked,
      ),
      switchMap(({ hotel }) =>
        this._taskManagementHttpService.getRoomCleanTaskList(hotel.entId).pipe(
          map(({ taskList, summary }) =>
            TasksActions.getRoomCleanTaskListSucceeded({ taskList, summary }),
          ),
          catchError((error) =>
            of(TasksActions.getRoomCleanTaskListFailed({ error })),
          ),
        ),
      ),
    ),
  );

  updatePMSStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.pmsStatusSaveButtonClicked),
      withLatestFrom(
        this._store.select(TasksSelectors.selectOpenRoomCleanTask),
      ),
      map(([{ roomStatusId }, task]) => ({
        roomStatusId,
        taskId: task?.id,
        entId: task?.entId,
        roomInventoryId: task?.details?.roomInventoryId,
        changeLinen: task?.details?.changeLinen,
      })),
      filterRequiredObject(),
      switchMap((body) =>
        this._housekeepingHttpService.updateRoomCleanDetails(body).pipe(
          withLatestFrom(this._store.select(UserSelectors.selectUser)),
          map(([payload, user]) => ({
            taskId: payload.taskId,
            roomStatusId: payload.roomStatusId,
            id: user?.WebUserId,
            firstName: user?.name.first,
            lastName: user?.name.last,
          })),
          filterRequiredObject(),
          map(({ taskId, roomStatusId, id, firstName, lastName }) =>
            TasksActions.updatePMSStatusSucceeded({
              taskId,
              roomStatusId,
              currentUser: {
                id,
                firstName,
                lastName,
              },
            }),
          ),
          catchError(() => of(TasksActions.updatePMSStatusFailed())),
        ),
      ),
    ),
  );

  markRoomAsStillDND$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(TasksActions.dndSignStillOnConfirmationButtonClicked),
        concatLatestFrom(() =>
          this._store.select(TasksSelectors.selectOpenRoomCleanTask),
        ),
        map(([_, task]) => ({
          taskId: task?.id,
          entId: task?.entId,
          taskStatusId: task?.statusId,
        })),
        filterRequiredObject(),
        switchMap((body) =>
          this._taskManagementHttpService
            .updateTaskStatus(body)
            .pipe(catchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  );

  updateChangeLinen$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.changeLinenSaveButtonClicked),
      withLatestFrom(
        this._store.select(TasksSelectors.selectOpenRoomCleanTask),
      ),
      map(([{ changeLinen }, task]) => ({
        changeLinen,
        taskId: task?.id,
        entId: task?.entId,
        roomInventoryId: task?.details?.roomInventoryId,
        roomStatusId: task?.details?.roomStatusId,
      })),
      filterRequiredObject(),
      switchMap((body) =>
        this._housekeepingHttpService.updateRoomCleanDetails(body).pipe(
          withLatestFrom(this._store.select(UserSelectors.selectUser)),
          map(([payload, user]) => ({
            taskId: payload.taskId,
            changeLinen: payload.changeLinen ?? false,
            id: user?.WebUserId,
            firstName: user?.name.first,
            lastName: user?.name.last,
          })),
          filterRequiredObject(),
          map(({ taskId, changeLinen, id, firstName, lastName }) =>
            TasksActions.updateChangeLinenSucceeded({
              taskId,
              changeLinen,
              currentUser: {
                id,
                firstName,
                lastName,
              },
            }),
          ),
          catchError(() => of(TasksActions.updateChangeLinenFailed())),
        ),
      ),
    ),
  );

  updateRoomCleanTaskAssignee$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.roomCleanAssigneeSaveButtonClicked),
      withLatestFrom(
        this._store.select(TasksSelectors.selectOpenRoomCleanTask),
      ),
      map(([{ assigneeId, assigneeTypeId }, task]) => ({
        taskId: task?.id,
        entId: task?.entId,
        assigneeId,
        assigneeTypeId,
      })),
      filterRequiredObject(),
      switchMap((body) =>
        this._taskManagementHttpService.updateRoomCleanTaskAssignee(body).pipe(
          map((payload) =>
            TasksActions.updateRoomCleanTaskAssigneeSucceeded({
              task: payload.task,
            }),
          ),
          catchError(() =>
            of(TasksActions.updateRoomCleanTaskAssigneeFailed()),
          ),
        ),
      ),
    ),
  );

  updateTaskStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.roomCleanTaskStatusSaveButtonClicked),
      withLatestFrom(
        this._store.select(TasksSelectors.selectOpenRoomCleanTask),
      ),
      map(([{ taskStatusId }, task]) => ({
        taskId: task?.id,
        entId: task?.entId,
        taskStatusId,
      })),
      filterRequiredObject(),
      switchMap(({ taskId, entId, taskStatusId }) =>
        this._taskManagementHttpService
          .updateTaskStatus({
            taskId,
            entId,
            taskStatusId,
          })
          .pipe(
            map((payload) =>
              TasksActions.updateTaskStatusSucceeded({
                taskId,
                payload,
              }),
            ),
            catchError(() => of(TasksActions.updateTaskStatusFailed())),
          ),
      ),
    ),
  );

  updateRoomCleanTaskStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.updateTaskStatusActionSheetOptionClicked),
      switchMap((action) => {
        const isTaskRoute = this._router.url.startsWith('/app/tasks');
        return this._store
          .select(
            TasksSelectors.selectRealtimeRoomsUpdateTaskStatusBody(
              action.statusType,
              isTaskRoute,
            ),
          )
          .pipe(first());
      }),
      filterRequiredObject(),
      switchMap((body) => {
        return this._taskManagementHttpService.updateTaskStatus(body).pipe(
          map((updatedTaskStatusData) =>
            TasksActions.updateTaskStatusSucceeded({
              taskId: body.taskId,
              payload: updatedTaskStatusData,
            }),
          ),
          catchError(() => of(TasksActions.updateTaskStatusFailed())),
        );
      }),
    ),
  );

  fetchRealtimeRoomsStatus$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        TasksActions.defaultRealtimeRoomsMenuDetermined,
        TasksActions.realtimeRoomsMenuSubmitted,
      ),
      switchMap(({ hotel }) =>
        this._housekeepingHttpService.getRealtimeRoomsStatus(hotel.entId).pipe(
          map((payload) =>
            TasksActions.fetchRealtimeRoomsStatusSucceeded({
              status: payload,
            }),
          ),
          catchError(() => of(TasksActions.fetchRealtimeRoomsStatusFailed())),
        ),
      ),
    ),
  );

  createOrEditTaskNote$ = createEffect(() =>
    this._actions$.pipe(
      ofType(TasksActions.saveNoteclicked),
      switchMap((action) => {
        const entIdSelector = this._router.url.startsWith('/app/tasks')
          ? TasksSelectors.selectEntId
          : TasksSelectors.selectRealtimeRoomsEntId;
        return of(action).pipe(
          withLatestFrom(
            this._store.select(entIdSelector),
            this._store.select(TasksSelectors.selectActiveTaskId),
          ),
        );
      }),
      map(([action, entId, taskId]) => ({
        value: action.value,
        entId: entId as string,
        id: action.id,
        taskId,
      })),
      switchMap((body: CreateOrEditRoomCleanTaskNoteBody) =>
        this._taskManagementHttpService.createOrEditTaskNote(body).pipe(
          map((note) =>
            TasksActions.createOrEditTaskNoteSucceeded({
              note,
              taskId: body.taskId,
            }),
          ),
          tap((action) => {
            const isTaskRoute = this._router.url.startsWith('/app/tasks');
            const route = isTaskRoute
              ? '/app/tasks/view-room-clean'
              : '/app/housekeeping-tools/realtime-rooms/view-room-clean';
            this._navController.navigateBack(route, {
              queryParams: { taskId: action.taskId },
            });
          }),
          catchError(() => of(TasksActions.createOrEditTaskNoteFailed())),
        ),
      ),
    ),
  );

  markAllNotesAsRead$ = createEffect(
    () =>
      this._actions$.pipe(
        ofType(TasksActions.roomCleanTaskNoteViewed),
        switchMap(({ id }) =>
          this._taskManagementHttpService
            .markAllNotesAsRead(id)
            .pipe(catchError(() => EMPTY)),
        ),
      ),
    { dispatch: false },
  );

  constructor(
    private _actions$: Actions,
    private _taskManagementHttpService: TaskManagementHttpService,
    private _store: Store<State>,
    private _loadingController: LoadingController,
    private _navController: NavController,
    private _router: Router,
    private _housekeepingHttpService: HousekeepingHttpService,
    private _hotelsService: HotelsService,
    private _taskService: TaskService,
  ) {}

  private _showLoadingOverlay(): void {
    this._loadingController.getTop().then((loadingElement) => {
      if (!loadingElement) {
        this._loadingController.create().then((loadingOverlay) => {
          loadingOverlay.present();
        });
      }
    });
  }

  private _hideLoadingOverlay(): void {
    this._loadingController.getTop().then((loadingElement) => {
      if (loadingElement) {
        loadingElement.dismiss();
      }
    });
  }
}
